"use client";

import type React from "react";
import type { Control } from "react-hook-form";
import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema.ts";

import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { InformationTooltip } from "@/components/InformationTooltip.tsx";

interface BasicInfoStepProps {
  control: Control<CreateProfileFormData>;
}

export function BasicInfoStep({ control }: BasicInfoStepProps) {
  const { t } = useTranslation(["profile", "common"]);
  const [headlineLength, setHeadlineLength] = useState(0);
  const maxHeadlineLength = 300;
  const headlineProgress = (headlineLength / maxHeadlineLength) * 100;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 12,
      },
    },
  };

  return (
    <motion.div
      animate="visible"
      className="mx-auto mb-8 w-full max-w-3xl px-4 py-6 md:px-6"
      initial="hidden"
      variants={containerVariants}
    >
      <motion.div className="mb-8 text-center" variants={itemVariants}>
        <h1 className="mb-3 text-2xl font-bold sm:text-3xl">{t("profile:basicInfo.sideTitle")}</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm sm:text-base">
          {t("profile:basicInfo.description")}
        </p>
      </motion.div>

      <motion.div className="space-y-8" variants={containerVariants}>
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <motion.div variants={itemVariants}>
              <FormItem className="space-y-3">
                <FormLabel className="text-accent-foreground flex items-center gap-2 text-base font-medium sm:text-lg">
                  <Icon height={"1.25rem"} icon="mdi:account" width={"1.25rem"} />
                  {t("profile:basicInfo.name")}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-side-accent/50 h-12 rounded-xl text-base transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                    placeholder={t("profile:basicInfo.namePlaceholder")}
                    type="text"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </motion.div>
          )}
        />

        <FormField
          control={control}
          name="headline"
          render={({ field }) => (
            <motion.div variants={itemVariants}>
              <FormItem className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-accent-foreground flex items-center gap-2 text-base font-medium sm:text-lg">
                    <Icon height={"1.25rem"} icon="mdi:format-title" width={"1.25rem"} />
                    {t("profile:basicInfo.title")}
                  </FormLabel>
                  <span className="text-muted-foreground text-sm">
                    {headlineLength}/{maxHeadlineLength}
                  </span>
                </div>
                <FormControl>
                  <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                    <CardContent className="p-0">
                      <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                        <Textarea
                          className="bg-side-accent/50 [field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                          placeholder={t("profile:basicInfo.titlePlaceholder")}
                          {...field}
                          maxLength={maxHeadlineLength}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setHeadlineLength(e.target.value.length);
                          }}
                        />
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </FormControl>
                <div className="space-y-1.5">
                  <div className="text-muted-foreground flex items-center justify-between px-1 text-xs">
                    <span>
                      {t("profile:basicInfo.charactersLeft", {
                        count: maxHeadlineLength - headlineLength,
                      })}
                    </span>
                    <span>{Math.round(headlineProgress)}%</span>
                  </div>
                  <Progress
                    className="h-1.5 rounded-full"
                    style={
                      {
                        background: "var(--muted)",
                        "--progress-value": `${headlineProgress}%`,
                      } as React.CSSProperties
                    }
                    value={headlineProgress}
                  />
                  <FormMessage />
                </div>
              </FormItem>
            </motion.div>
          )}
        />

        <FormField
          control={control}
          name="youtube_url"
          render={({ field }) => (
            <motion.div variants={itemVariants}>
              <FormItem className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-accent-foreground flex items-center gap-2 text-base font-medium sm:text-lg">
                    <Icon
                      className="text-red-500"
                      height={"1.25rem"}
                      icon="mdi:youtube"
                      width={"1.25rem"}
                    />
                    {t("profile:basicInfo.youtubeUrl")}
                    <InformationTooltip
                      description={t("profile:basicInfo.videoTip")}
                      iconSize={"1.25rem"}
                    />
                  </FormLabel>
                  <span className="text-muted-foreground bg-muted/40 rounded-full px-2 py-0.5 text-sm text-xs">
                    {t("common:form.optional")}
                  </span>
                </div>
                <FormControl>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Icon
                        className="text-muted-foreground"
                        height={"1.25rem"}
                        icon="mdi:link-variant"
                        width={"1.25rem"}
                      />
                    </div>
                    <Input
                      {...field}
                      className="bg-side-accent/50 h-12 rounded-xl pl-10 text-base transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
                      placeholder={t("profile:basicInfo.youtubeUrlPlaceholder")}
                      type="url"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            </motion.div>
          )}
        />
      </motion.div>
    </motion.div>
  );
}
